import React from "react";
import { Box, Paper, Typography, Stack, Button, Divider } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PhoneIcon from "@mui/icons-material/Phone";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

/** ----------------- helpers ----------------- */
const isVideoUrl = (url = "") =>
  /\.(mp4|m4v|mpeg|webm|h263|m4p)(\?|#|$)/i.test(url);

const iconFor = (t) => {
  const type = (t || "").toLowerCase();
  if (type === "url_action") return <OpenInNewIcon fontSize="small" />;
  if (type === "dialer_action" || type === "dialer_action")
    return <PhoneIcon fontSize="small" />;
  return <ChatBubbleOutlineIcon fontSize="small" />; // reply / default
};

// Normalize suggestion objects coming from editor or API
const normButtons = (list = []) =>
  (Array.isArray(list) ? list : []).map((s) => ({
    suggestionType: s.suggestionType || s.type || "reply",
    text: s.displayText ?? s.text ?? "",
    url: s.url,
    phoneNumber: s.phoneNumber,
  }));

/** ----------------- building blocks ----------------- */
const TextBlock = ({ textMessage = "", suggestions = [] }) => {
  const buttons = normButtons(suggestions);
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#232323",
        borderColor: "#333",
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: "#e6e6e6",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            minHeight: 60,
          }}
        >
          {textMessage?.trim() || "Your message preview will appear here."}
        </Typography>
      </Box>

      {buttons.length > 0 && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Divider sx={{ mb: 1.5, borderColor: "#333" }} />
          <Stack spacing={0.5} alignItems="center">
            {buttons.map((s, i) => (
              <Button
                key={i}
                variant="text"
                size="small"
                startIcon={iconFor(s.suggestionType)}
                disableRipple
                sx={{
                  alignSelf: "center",
                  justifyContent: "center",
                  color: "#9fc1ff",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 0,
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                {s.text?.trim() || "Button"}
              </Button>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

const RichCard = ({
  card,
  orientation = "VERTICAL",
  height = "SHORT",
}) => {
  const H = height === "TALL" ? 280 : 170; // image/video box height hint
  const isVertical = String(orientation).toUpperCase() !== "HORIZONTAL";
  const suggestions = normButtons(card?.suggestions);
  const looksDynamic = (card?.mediaUrl || "").includes("[img");
  const isVid = isVideoUrl(card?.mediaUrl || "");

  const Media = (
    <div
      style={{
        width: isVertical ? "100%" : 180,
        height: isVertical ? H : "100%",
        overflow: "hidden",
        backgroundColor: "#151515",
        flexShrink: 0,
      }}
    >
      {card?.mediaUrl ? (
        looksDynamic ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              color: "#888",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              padding: 8,
              textAlign: "center",
            }}
          >
            Dynamic media token
            <br />
            {card.mediaUrl}
          </div>
        ) : isVid ? (
          <video
            src={card.mediaUrl}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            muted
            controls
            playsInline
          />
        ) : (
          <img
            src={card.mediaUrl}
            alt="card media"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        )
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            color: "#666",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
          }}
        >
          Media
        </div>
      )}
    </div>
  );

  const Body = (
    <div style={{ padding: 12, minWidth: 0 }}>
      {(card?.cardTitle || card?.cardDescription) && (
        <div style={{ marginBottom: 8 }}>
          {card?.cardTitle && (
            <Typography
              variant="subtitle2"
              sx={{ color: "#fff", fontWeight: 600, lineHeight: 1.2 }}
            >
              {card.cardTitle}
            </Typography>
          )}
          {card?.cardDescription && (
            <Typography
              variant="body2"
              sx={{
                color: "#e6e6e6",
                mt: card?.cardTitle ? 0.5 : 0,
                whiteSpace: "pre-wrap",
              }}
            >
              {card.cardDescription}
            </Typography>
          )}
        </div>
      )}

      {suggestions.length > 0 && (
        <div>
          <Divider sx={{ mb: 1, borderColor: "#333" }} />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              rowGap: 4,
              alignItems: "center",
            }}
          >
            {suggestions.map((s, i) => (
              <Button
                key={i}
                variant="text"
                size="small"
                startIcon={iconFor(s.suggestionType)}
                disableRipple
                sx={{
                  alignSelf: "center",
                  justifyContent: "center",
                  color: "#9fc1ff",
                  textTransform: "none",
                  fontWeight: 500,
                  px: 0,
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                {s.text?.trim() || "Button"}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#232323",
        borderColor: "#333",
      }}
    >
      {isVertical ? (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ width: "100%", height: H }}>{Media}</div>
          {Body}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            minHeight: H,
          }}
        >
          {Media}
          <div style={{ flex: 1 }}>{Body}</div>
        </div>
      )}
    </Paper>
  );
};

const Carousel = ({ cards = [], height = "SHORT", width = "MEDIUM" }) => {
  const cardWidth = String(width).toUpperCase() === "FULL" ? 420 : 320;
  const trackStyle = {
    display: "flex",
    flexDirection: "row",
    columnGap: 12,
    overflowX: "auto",
    paddingBottom: 4,
    scrollSnapType: "x mandatory",
  };
  const itemStyle = {
    flex: "0 0 auto",
    width: cardWidth,
    scrollSnapAlign: "start",
  };

  return (
    <div style={trackStyle}>
      {cards.map((c, idx) => (
        <div key={idx} style={itemStyle}>
          <RichCard card={c} orientation="VERTICAL" height={height} />
        </div>
      ))}
    </div>
  );
};

/** ----------------- main preview ----------------- */
function MessagePreview({ form }) {
  const type = form?.type || "text";

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: "#1f1f1f",
          color: "#fff",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ textAlign: "center", mb: 1.5, opacity: 0.9 }}
        >
          Message Preview
        </Typography>

        {type === "text" && (
          <TextBlock
            textMessage={form?.textMessage}
            suggestions={form?.suggestions}
          />
        )}

        {type === "richcard" && (
          <RichCard
            card={{
              cardTitle: form?.standaloneCard?.cardTitle,
              cardDescription: form?.standaloneCard?.cardDescription,
              mediaUrl: form?.standaloneCard?.mediaUrl,
              suggestions: form?.standaloneCard?.suggestions,
            }}
            orientation={form?.orientation || "VERTICAL"}
            height={form?.height || "SHORT"}
          />
        )}

        {type === "carousel" && (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "#232323",
              borderColor: "#333",
            }}
          >
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: "#bdbdbd", letterSpacing: 0.3 }}
              >
                Swipeable Carousel
              </Typography>
            </Box>
            <Box sx={{ px: 2, pb: 2 }}>
              <Carousel
                cards={Array.isArray(form?.carouselCard) ? form.carouselCard : []}
                height={form?.height || "SHORT"}
                width={form?.width || "MEDIUM"}
              />
            </Box>
          </Paper>
        )}
      </Paper>
    </Box>
  );
}

export default MessagePreview;
